<template>
  <el-card class="box-card">
    <div
        slot="header"
        class="clearfix">
      <span class="header-item">Firmwares</span>
      <span class="ring-card__header-buttons">
        <RefreshButton
            :disabled="!!firmwareSupportMsg"
            @click.native="getData" />
      </span>
    </div>
    <h1
        v-if="firmwareSupportMsg"
        style="text-align: center;">
      {{ firmwareSupportMsg }}
    </h1>
    <div
        v-else
        v-loading="loading"
        class="card-content">
      <el-row
          :gutter="10"
          class="firmware-components-wrapper">
        <el-col
            v-for="(comp, index) in components"
            :key="index"
            :span="8"
            class="firmware-component-input">
          <FirmwareComponentInput
              :edit-mode="false"
              :options="firmwaresOptions.components[comp.name]"
              :component="comp"
              :default-firmware="defaultFirmware(comp.name)"
              :firmware-assigned="currentFirmware(comp.name)" />
        </el-col>
      </el-row>
    </div>
  </el-card>
</template>

<script>
import FirmwareComponentInput from "@/containers/Customer/Devices/Firmwares/FirmwareComponentInput";
import RefreshButton from "@/containers/Customer/RefreshButton";
import { createNamespacedHelpers } from "vuex";
const { mapGetters, mapActions } = createNamespacedHelpers("db/device");
import { get, startCase } from "lodash";

export default {
  name: "DeviceFirmware",
  components: { FirmwareComponentInput, RefreshButton },
  props: {
    device: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      firmwareSupportMsg: null,
      loading: false,
      currentFirmwares: [],
      components: [],
      firmwares: [],
      defaults: [],
      firmwaresOptions: {
        loading: false,
        loaded: false,
        components: {},
      },
    };
  },
  computed: {
    ...mapGetters(["lastStatusReport"]),
  },
  watch: {
    "device.doorbot_id": {
      handler: "init",
      immediate: true,
    },
  },
  created() {
    this.firmwareSupportMsg = null;
  },
  methods: {
    init(value, old_value) {
      if (value && value === old_value) return;
      try {
        this.getData();
      } catch (err) {
        this.firmwareSupportMsg = "This Product is not supported yet!";
        this.$notify.warning({
          title: startCase(this.device.type),
          message: "Firmware Not Supported",
          duration: 7000,
        });
        return;
      }
    },
    ...mapActions([
      "getFirmwares",
      "loadDefaultsFirmwares",
      "fetchAvailableFirmwares",
      "fetchFirmwareComponents",
      "loadLastStatusReport",
    ]),
    currentFirmware(component) {
      return (
        this.currentFirmwares.find((f) => {
          return f.component === component;
        }) || {}
      );
    },
    defaultFirmware(component) {
      return (
        this.defaults.find((f) => {
          return f.component === component;
        }) || {}
      );
    },
    getData() {
      this.loading = true;
      this.loadFirmwareComponents().then(() => {
        this.loadAvailableFirmwares();
        this.loadDeviceFirmwares();
        this.loadDefaultsList();
      });
    },
    loadFirmwareComponents() {
      return this.fetchFirmwareComponents({ type: this.device.type })
        .then((componentNames) => {
          if (componentNames && Array.isArray(componentNames)) {
            const components = componentNames.map((name) => {
              return {
                name,
                newVersionSelected: null,
                type: this.device.type,
              };
            });
            this.components = components;
          } else {
            this.components = [];
          }
        })
        .catch((err) => {
          console.error(err);
          this.components = [];
        });
    },
    loadAvailableFirmwares() {
      this.firmwaresOptions.loading = true;
      this.firmwaresOptions.loaded = false;
      let components = this.components.map((c) => c.name);
      this.fetchAvailableFirmwares({
        type: this.device.type,
        components,
        doorbotId: this.device.doorbot_id,
      })
        .then((components) => {
          this.firmwaresOptions.components = components;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.firmwaresOptions.loading = false;
          this.firmwaresOptions.loaded = true;
        });
    },
    loadDeviceFirmwares() {
      this.loading = true;
      this.getFirmwares({ doorbotId: this.device.doorbot_id })
        .then((response) => {
          let currentFirmwares = [];
          if (response && response.data) {
            if (response.data["dr_assigned_components"]) {
              currentFirmwares = Object.keys(
                response.data.dr_assigned_components
              ).map((component) => {
                return {
                  component: component,
                  version: response.data.dr_assigned_components[component],
                };
              });
            } else if (response.data["firmwares"]) {
              currentFirmwares = response.data.firmwares;
            }
          }
          this.currentFirmwares = currentFirmwares;
        })
        .catch((error) => {
          console.log(error);
          this.currentFirmwares = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadDefaultsList() {
      this.loading = true;
      this.loadDefaultsFirmwares({
        type: this.device.type,
        doorbotId: this.device.doorbot_id,
      })
        .then((response) => {
          let defaults = get(response, "data", []);
          this.defaults = defaults || [];
        })
        .catch((error) => {
          console.log(error);
          this.defaults = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.firmwares-controls {
  border-top: 1px solid #cacaca;
  margin-top: 20px;
  padding: 10px 0px 0px 0px;
  height: 45px;
  line-height: 40px;
}

.firmwares-controls .el-switch__label * {
  font-size: 15px;
}
.firmware-component-input {
  padding: 0 !important;
  border-right: thin solid $--color-silver;
}
.firmware-components-wrapper {
  margin: 0;
  margin-right: 40px;
}
</style>
